import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import Cookies from "js-cookie";
import useAuth from "../../hook/useAuth";
import { downloadMemberImportSampleCsv } from "../../utils/memberImportCsv";
import useConfirm from "../../hook/useConfirm";
import useGroup from "../../hook/useGroup";
import { post_BulkFamilyMemberImports, post_BulkMemberImports } from "../../services/BulkImport";

type ImportAction = "INSERT" | "UPDATE" | "SKIP";

const ALLOWED_EXTENSIONS = [".csv", ".xls", ".xlsx"];

type ImportType = "family" | "familyMembers";

type ImportViewProps = {
    importType: ImportType;
};

const ImportView = ({ importType }: ImportViewProps) => {
    const token = Cookies.get("token-my-cdir");
    const { auth }: any = useAuth();
    const { selectedGroupId }: any = useGroup();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();

    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isImportMenuOpen, setIsImportMenuOpen] = useState<boolean>(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleDropDown = (e: MouseEvent) => {
            if (!dropdownRef.current?.contains(e.target as Node)) {
                setIsImportMenuOpen(false);
            }
        }

        document.addEventListener("click", handleDropDown);

        return () => {
            document.removeEventListener("click", handleDropDown);
        };
    }, []);

    const getFileExtension = (fileName: string) => {
        const lastDot = fileName.lastIndexOf(".");

        if (lastDot === -1) {
            return "";
        }

        return fileName.substring(lastDot).toLowerCase();
    }

    const handleFileSelect = (file: File) => {
        const extension = getFileExtension(file.name);
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
            setConfirmData({
                title: 'Warning',
                summary: "Invalid file type. Please upload a CSV, XLS, or XLSX file.",
                confirmBtn: 'Ok',
                isConfirm: (callback: any) => {
                    callback();
                }
            });
            setConfirmPopup(true);
            return;
        }

        setSelectedFile(file);
    }

    const handleBrowseFile = () => {
        fileInputRef.current?.click();
    }

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) {
            handleFileSelect(file);
        }

        // Allows selecting the same file again.
        event.target.value = "";
    }

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setIsDragging(true);
    }

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setIsDragging(false);
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        setIsDragging(false);

        const file = event.dataTransfer.files?.[0];

        if (file) {
            handleFileSelect(file);
        }
    }

    const getStringValue = (value: unknown): string | undefined => {
        if (
            value === null ||
            value === undefined ||
            value === ""
        ) {
            return undefined;
        }

        const result = String(value).trim();

        return result || undefined;
    }

    const mapFamilyMemberRow = (row: Record<string, unknown>): any => {
        return {
            firstName: getStringValue(row.FirstName),
            lastName: getStringValue(row.LastName),
            emailAddress: getStringValue(
                row["EmailAddress (Required)"] ?? row.EmailAddress
            ),
            userName: getStringValue(row.UserName),
            phoneNumber: getStringValue(row.PhoneNumber),
            birthday: getStringValue(row.Birthday) ?? null,
            groupId: selectedGroupId,
            memberName: getStringValue(row.MemberName),
            relationToMember: getStringValue(row.RelationToMember),
            importAction: "INSERT" as ImportAction,
        };
    };

    const mapFamilyRow = (row: Record<string, unknown>): any => {
        const importAction = "INSERT" as ImportAction;

        return {
            memberNumber: getStringValue(row.MemberNumber),
            familyName: getStringValue(row.FamilyName),
            firstName: getStringValue(row.FirstName),
            lastName: getStringValue(row.LastName),
            emailAddress: getStringValue(row.EmailAddress),
            phoneNumber: getStringValue(row.PhoneNumber),
            imagePath: getStringValue(row.ImagePath),
            street: getStringValue(row.Street),
            street2: getStringValue(row.Street2),
            city: getStringValue(row.City),
            state: getStringValue(row.State),
            country: getStringValue(row.Country),
            zip: getStringValue(row.Zip),
            birthday: getStringValue(row.Birthday) ?? null,
            groupId: selectedGroupId,
            importAction
        };
    }

    const parseMemberFile = async (file: File): Promise<any[]> => {
        const arrayBuffer = await file.arrayBuffer();

        const workbook = XLSX.read(arrayBuffer, {
            type: "array",
            cellDates: true,
        });

        if (!workbook.SheetNames.length) {
            throw new Error("The file does not contain any worksheet.");
        }

        const worksheet = workbook.Sheets[workbook.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
            worksheet,
            {
                defval: "",
                raw: false,
            }
        );

        if (!rows.length) {
            throw new Error("The selected file does not contain any records.");
        }

        return importType === "familyMembers"
            ? rows.map(mapFamilyMemberRow)
            : rows.map(mapFamilyRow);
    };

    const handleImportFile = async () => {
        if (!selectedFile) return;

        try {
            setIsImporting(true);

            const records = await parseMemberFile(selectedFile);

            const insertRows = records.filter(
                (record) => record.importAction === "INSERT"
            );

            if (!insertRows.length) {
                throw new Error("No records are available to import.");
            }

            if (importType === "familyMembers") {
                await post_BulkMemberImports(
                    insertRows,
                    auth.id,
                    auth?.givenname || "",
                    token!
                );
            } else {
                await post_BulkFamilyMemberImports(
                    insertRows,
                    auth.id,
                    auth?.givenname || "",
                    token!
                );
            }

            const recordType =
                importType === "familyMembers" ? "family member" : "family";

            setConfirmData({
                title: "Info!",
                summary: [
                    "Your file has been uploaded successfully.",
                    "",
                    `Total records: ${insertRows.length}`,
                    "",
                    `Please note: The ${recordType} records have not been added to the table yet.`,
                    "Our support team will review and process your uploaded file.",
                    "You will be notified once the records have been added.",
                ].join("\n"),
                confirmBtn: "Ok",
                isConfirm: (callback: any) => {
                    callback();
                },
            });

            setConfirmPopup(true);
            setSelectedFile(null);
            setIsImportModalOpen(false);
        } catch (error) {
            console.error("Import failed:", error);

            setConfirmData({
                title: "Warning",
                summary:
                    error instanceof Error
                        ? error.message
                        : "Failed to import the file.",
                confirmBtn: "Ok",
                isConfirm: (callback: any) => {
                    callback();
                },
            });

            setConfirmPopup(true);
        } finally {
            setIsImporting(false);
        }
    };

    const handleCloseImportModal = () => {
        if (isImporting) {
            return;
        }

        setIsImportModalOpen(false);
        setSelectedFile(null);
        setIsDragging(false);
    }

    return (
        <div
            ref={dropdownRef}
            className=" relative"
        >
            <button
                type="button"
                onClick={() => setIsImportMenuOpen((previous) => !previous)}
                disabled={isImporting}
                className="flex items-center justify-between gap-2 w-full xm:w-fit bg-secondary px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
                {importType === "family" ? "Import Family" : "Import Members"}

                <svg
                    className={`h-4 w-4 transition-transform ${isImportMenuOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {
                isImportMenuOpen && (
                    <div className="absolute right-0 z-50 mt-2 w-56 overflow-hidden border border-gray-200 bg-white shadow-lg">
                        <button
                            type="button"
                            onClick={() => {
                                downloadMemberImportSampleCsv(importType);
                                setIsImportMenuOpen(false);
                            }}
                            className="flex w-full items-center px-4 py-2.5 text-left text-sm hover:bg-gray-100"
                        >
                            Download Sample CSV
                        </button>

                        <button
                            type="button"
                            onClick={() => {
                                setIsImportMenuOpen(false);
                                setSelectedFile(null);
                                setIsImportModalOpen(true);
                            }}
                            disabled={isImporting}
                            className="flex w-full items-center px-4 py-2.5 text-left text-sm hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Import CSV / Excel
                        </button>
                    </div>
                )
            }

            {
                isImportModalOpen && (
                    <div
                        className="
                            fixed
                            inset-0
                            z-[9999]
                            flex
                            items-start
                            justify-center
                            overflow-y-auto
                            bg-black/50
                            p-4                          
                            sm:p-6
                        "
                        onClick={handleCloseImportModal}
                    >
                        <div
                            className="
                                my-2
                                flex
                                w-full
                                max-w-lg
                                flex-col
                                overflow-hidden                             
                                bg-white
                                shadow-xl
                                sm:my-6
                                max-h-[calc(100dvh-1rem)]
                                sm:max-h-[calc(100dvh-3rem)]
                            "
                            onClick={(event) => event.stopPropagation()}
                        >
                            <div className="flex shrink-0 items-center justify-between border-b px-4 py-4 sm:px-6">
                                <div className="min-w-0">
                                    <h2 className="text-base font-semibold text-gray-900 sm:text-lg">
                                        {importType === "family" ? "Import Family" : "Import Members"}
                                    </h2>

                                    <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                                        Upload a CSV or Excel file
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleCloseImportModal}
                                    disabled={isImporting}
                                    className="
                                        ml-3
                                        shrink-0                                      
                                        p-2
                                        text-gray-400
                                        hover:bg-gray-100
                                        hover:text-gray-600
                                        disabled:cursor-not-allowed
                                        disabled:opacity-50
                                    "
                                >
                                    <svg
                                        className="h-5 w-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv,.xls,.xlsx"
                                    className="hidden"
                                    onChange={handleFileInputChange}
                                />

                                <div
                                    onDragOver={handleDragOver}
                                    onDragEnter={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={handleBrowseFile}
                                    className={`
                                        flex
                                        min-h-[180px]
                                        cursor-pointer
                                        flex-col
                                        items-center
                                        justify-center                                
                                        border-2
                                        border-dashed
                                        px-4
                                        py-6
                                        text-center
                                        transition-colors
                                        sm:min-h-[220px]
                                        sm:px-6
                                        sm:py-8
                                        ${isDragging
                                            ? "border-secondary bg-secondary/5"
                                            : "border-gray-300 hover:border-secondary hover:bg-gray-50"
                                        }
                                    `}
                                >
                                    {
                                        selectedFile ? (
                                            <>
                                                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary/10 text-secondary sm:mb-4 sm:h-14 sm:w-14">
                                                    <svg
                                                        className="h-6 w-6 sm:h-7 sm:w-7"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1.8}
                                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                        />
                                                    </svg>
                                                </div>

                                                <p className="max-w-[90%] truncate text-sm font-medium text-gray-900">
                                                    {selectedFile.name}
                                                </p>

                                                <p className="mt-1 text-xs text-gray-500">
                                                    {(
                                                        selectedFile.size /
                                                        1024 /
                                                        1024
                                                    ).toFixed(2)}{" "}
                                                    MB
                                                </p>

                                                <button
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        setSelectedFile(null);
                                                    }}
                                                    className="mt-3 text-sm text-red-500 hover:underline"
                                                >
                                                    Remove file
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <div
                                                    className={`
                                                    mb-3
                                                    flex
                                                    h-12
                                                    w-12
                                                    items-center
                                                    justify-center
                                                    rounded-full
                                                    sm:mb-4
                                                    sm:h-14
                                                    sm:w-14
                                                    ${isDragging
                                                            ? "bg-secondary/10 text-secondary"
                                                            : "bg-gray-100 text-gray-500"
                                                        }
                                                `}
                                                >
                                                    <svg
                                                        className="h-6 w-6 sm:h-7 sm:w-7"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1.8}
                                                            d="M7 16a4 4 0 01-.88-7.903A5.002 5.002 0 0116.9 6H17a5 5 0 011 9.9M12 12v9m0-9l-3 3m3-3l3 3"
                                                        />
                                                    </svg>
                                                </div>

                                                <p className="text-sm font-medium text-gray-900">
                                                    Drag & drop your file here
                                                </p>

                                                <p className="mt-1 text-sm text-gray-500">
                                                    or{" "}
                                                    <span className="font-medium text-secondary">
                                                        click to browse
                                                    </span>
                                                </p>

                                                <p className="mt-2 text-xs text-gray-400">
                                                    CSV, XLS, XLSX
                                                </p>
                                            </>
                                        )
                                    }
                                </div>
                            </div>

                            <div className="flex shrink-0 flex-col-reverse gap-2 border-t px-4 py-4 sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:px-6">
                                <button
                                    type="button"
                                    onClick={handleCloseImportModal}
                                    disabled={isImporting}
                                    className="
                                        w-full                                       
                                        border
                                        border-gray-300
                                        px-4
                                        py-2
                                        text-sm
                                        text-gray-700
                                        hover:bg-gray-50
                                        disabled:cursor-not-allowed
                                        disabled:opacity-50
                                        sm:w-auto
                                    "
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    onClick={handleImportFile}
                                    disabled={!selectedFile || isImporting}
                                    className="
                                        w-full                                     
                                        bg-secondary
                                        px-4
                                        py-2
                                        text-sm
                                        text-white
                                        hover:opacity-90
                                        disabled:cursor-not-allowed
                                        disabled:opacity-50
                                        sm:w-auto
                                    "
                                >
                                    {isImporting
                                        ? "Importing..."
                                        : importType === "family"
                                            ? "Import Family"
                                            : "Import Members"}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default ImportView;